import React, {useEffect, useState} from "react";
import {useSelector, useDispatch} from "react-redux";
import Toast from "cogo-toast";
import {SET_DASHBOARD_RANGE} from "../../../reducers/dashboardRange";
import {DefaultLoader} from "../../../components/DefaultLoader";
import {FiBarChart2, FiRefreshCw, FiServer} from "react-icons/fi";
import {apiGetDashboardInfo} from "./Actions";
import PageHeader from "../components/PageHeader";
import CustomTabs from "../components/CustomTabs";
import Filter from "../components/Filter";
import {FilterButton} from "../components/Button";
import DateTimeField from "../../../FinalForm/DatePickerField";
import moment from "moment/moment";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
    PieChart, Pie, Cell, Legend, LabelList
} from "recharts";

// ─── sub-components ───────────────────────────────────────────────────────────

const SectionLabel = ({children, first}) => (
    <div style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"8px",marginTop: first ? 0 : "18px"}}>
        {children}
    </div>
);

const ChartCard = ({title, children}) => (
    <div style={{border:"1px solid #e2e8f0",borderRadius:"12px",background:"#fff",padding:"16px",boxShadow:"0 1px 4px rgba(0,0,0,0.06)",marginBottom:"12px"}}>
        <div style={{fontSize: "var(--enigoo-fs-sm)",fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:"14px"}}>
            {title}
        </div>
        {children}
    </div>
);

// Pro DB (jediný binární stav OK/ERROR, žádné "config vs. live"): "OK" zelená, cokoliv
// jiné červená.
const SERVICE_STATE = {
    OK:  {label: "ZAPNUTO", border: "#bbf7d0", borderLeft: "#22c55e", bg: "#f0fdf4", dot: "#22c55e", text: "#15803d"},
};
const serviceState = (value) => SERVICE_STATE[value] ?? {label: value ?? "—", border: "#fecaca", borderLeft: "#ef4444", bg: "#fff5f5", dot: "#ef4444", text: "#dc2626"};

const ServiceCard = ({label, value}) => {
    const s = serviceState(value);
    return (
        <div style={{
            border: `1px solid ${s.border}`,
            borderLeft: `4px solid ${s.borderLeft}`,
            borderRadius:"10px",
            background: s.bg,
            padding:"14px",
            display:"flex",
            flexDirection:"column",
            gap:"6px",
        }}>
            <span style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:600,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</span>
            <div style={{display:"flex",alignItems:"center",gap:"7px"}}>
                <span style={{
                    width:"10px", height:"10px", borderRadius:"50%", flexShrink:0,
                    background: s.dot,
                    boxShadow: value === "OK" ? `0 0 6px ${s.dot}` : "none",
                }}/>
                <span style={{fontSize: "var(--enigoo-fs-xl)",fontWeight:700,color: s.text}}>{s.label}</span>
            </div>
        </div>
    );
};

const SOCKET_LABEL = {
    TCP: "TCP 9876 (turnikety/čtečky)",
    UDP: "UDP 9877 (Elvis)",
    TICKET_VALIDATOR: "TCP 9878 (ticket validator)",
};

// state z backendu (SocketStatusService): RUNNING / DISABLED / DOWN / UNEXPECTED.
// Barva karty jde podle stavu; DISABLED je šedá (úmyslně vypnuto), ne červená — je to
// očekávaný stav, ne chyba. UNEXPECTED (mělo by být vypnuto, ale běží) je oranžová,
// odlišná od DOWN (mělo by běžet, ale neběží) — obojí je problém, ale jiného druhu.
const SOCKET_STATE_STYLE = {
    RUNNING:    {border: "#bbf7d0", borderLeft: "#22c55e", bg: "#f0fdf4"},
    DISABLED:   {border: "#e2e8f0", borderLeft: "#94a3b8", bg: "#f8fafc"},
    DOWN:       {border: "#fecaca", borderLeft: "#ef4444", bg: "#fff5f5"},
    UNEXPECTED: {border: "#fed7aa", borderLeft: "#f97316", bg: "#fff7ed"},
};

const factStyle = (ok) => ({fontWeight: 700, color: ok ? "#15803d" : "#94a3b8"});

/**
 * Dvě nezávislé věci vedle sebe, přesně jak to bylo cílem: "Config" = má podle tabulky
 * config běžet, "Ověřeno" = opravdu běží (OS-level bind probe na backendu, ne interní
 * flag socket serveru — ten dokázal lhát, viz zombie-port bug).
 */
const SocketCard = ({socket}) => {
    if (!socket) return null;
    const style = SOCKET_STATE_STYLE[socket.state] ?? SOCKET_STATE_STYLE.DOWN;
    return (
        <div style={{
            border: `1px solid ${style.border}`,
            borderLeft: `4px solid ${style.borderLeft}`,
            borderRadius: "10px",
            background: style.bg,
            padding: "14px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
        }}>
            <span style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:600,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.05em"}}>
                {SOCKET_LABEL[socket.name] ?? socket.name}
            </span>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:"var(--enigoo-fs-sm)"}}>
                <span style={{color:"#94a3b8"}}>Config:</span>
                <span style={factStyle(socket.expected)}>{socket.expected ? "ZAPNUTO" : "VYPNUTO"}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:"var(--enigoo-fs-sm)"}}>
                <span style={{color:"#94a3b8"}}>Ověřeno (live):</span>
                <span style={factStyle(socket.running)}>{socket.running ? "BĚŽÍ" : "NEBĚŽÍ"}</span>
            </div>
        </div>
    );
};

const ResourceCard = ({label, used, total, unit, percent}) => {
    const p = Math.min(100, Math.round(percent ?? 0));
    const barColor  = p > 85 ? "#ef4444" : p > 65 ? "#f59e0b" : "#22c55e";
    const trackColor = p > 85 ? "#fee2e2" : p > 65 ? "#fef3c7" : "#dcfce7";
    return (
        <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",padding:"14px",display:"flex",flexDirection:"column",gap:"10px"}}>
            <span style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:600,color:"#64748b",textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</span>
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between"}}>
                <span style={{fontSize: "var(--enigoo-fs-4xl)",fontWeight:700,color:"#1e293b",lineHeight:1}}>
                    {p}<span style={{fontSize: "var(--enigoo-fs-sm)",fontWeight:400,color:"#94a3b8",marginLeft:"2px"}}>%</span>
                </span>
                <span style={{fontSize: "var(--enigoo-fs-xs)",color:"#94a3b8"}}>{used} / {total} {unit}</span>
            </div>
            <div style={{width:"100%",height:"6px",borderRadius:"999px",background:trackColor}}>
                <div style={{height:"6px",borderRadius:"999px",width:p+"%",background:barColor,transition:"width 0.4s"}}/>
            </div>
        </div>
    );
};

const InfoCard = ({label, value, sub}) => (
    <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",padding:"14px",display:"flex",flexDirection:"column",gap:"3px"}}>
        <span style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</span>
        <span style={{fontSize: "var(--enigoo-fs-md)",fontWeight:600,color:"#1e293b"}}>{value ?? "—"}</span>
        {sub && <span style={{fontSize: "var(--enigoo-fs-xs)",color:"#94a3b8"}}>{sub}</span>}
    </div>
);

const StatCard = ({label, value}) => (
    <div style={{border:"1px solid #bfdbfe",borderRadius:"10px",background:"#eff6ff",padding:"16px",display:"flex",flexDirection:"column",gap:"4px"}}>
        <span style={{fontSize: "var(--enigoo-fs-xs)",fontWeight:600,color:"#3b82f6",textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</span>
        <span style={{fontSize: "var(--enigoo-fs-5xl)",fontWeight:700,color:"#1d4ed8",lineHeight:1}}>{value ?? "—"}</span>
    </div>
);

const NoData = () => (
    <div style={{padding:"28px",textAlign:"center",color:"#cbd5e1",fontSize: "var(--enigoo-fs-sm)",fontWeight:500}}>Žádná data</div>
);

const CHART_COLORS = ["#3b82f6","#10b981","#f59e0b","#8b5cf6","#ef4444","#06b6d4","#ec4899","#84cc16","#f97316","#6366f1"];

const HBarChart = ({data}) => {
    if (!data?.length) return <NoData/>;
    return (
        <div style={{width:"100%", height: Math.max(100, data.length * 38 + 20)}}>
            <ResponsiveContainer>
                <BarChart data={data} layout="vertical" margin={{top:4,right:50,left:0,bottom:4}}>
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{fontSize:11,fill:"#94a3b8"}}/>
                    <YAxis type="category" dataKey="name" axisLine={false} tickLine={false}
                           width={140} tick={{fontSize:11,fill:"#475569"}}/>
                    <RTooltip
                        contentStyle={{borderRadius:"8px",border:"1px solid #e2e8f0",fontSize: "var(--enigoo-fs-sm)",boxShadow:"0 4px 12px rgba(0,0,0,0.08)"}}
                        cursor={{fill:"#f8fafc"}}
                    />
                    <Bar dataKey="count" name="Počet" barSize={16} radius={[0,6,6,0]}>
                        {data.map((_,i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>
                        ))}
                        <LabelList dataKey="count" position="right" style={{fontSize:11,fill:"#64748b",fontWeight:600}}/>
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

const RESULT_LABELS = {
    NOT_FOUND:                    "Vstupenka nenalezena",
    INCORRECT_ZONE:               "Nesprávná zóna",
    CARD_USED:                    "Karta již použita",
    NOT_IN_ZONE:                  "Není v zóně",
    NOT_IN_PARENT_ZONE:           "Není v nadřazené zóně",
    FULL_CAPACITY:                "Plná kapacita",
    TICKET_ENTRANCE_BLOCKED:      "Vstup zablokován",
    NO_CREDIT:                    "Nedostatek kreditu",
    READING_BLOCKED:              "Čtení zablokováno",
    FACE_NOT_RECOGNIZED:          "Obličej nerozpoznán",
    UNKNOWN_TURNSTILE:            "Neznámý turniket",
    ENTRY_IN_AREA:                "Již v areálu",
    IN_ZONE:                      "Již v zóně",
    NO_TICKET_ENTRANCE_BLOCATION: "Bez blokace vstupu",
};

const labelResult = (name) => RESULT_LABELS[name] ?? name;

const CHANGE_LABELS = {
    CHANGE_BC:     "Změna BC",
    STORNO:        "Storno",
    CHANGE_DATA:   "Změna dat",
    DELETE_IMAGE:  "Smazání fotky",
    BLOCK:         "Blokace",
    UNBLOCK:       "Odblokování",
    CHIP_TRANSFER: "Přenos čipu",
};

const labelChange = (name) => CHANGE_LABELS[name] ?? name;

const FAIL_COLORS = ["#ef4444","#f97316","#eab308","#8b5cf6","#06b6d4","#ec4899","#84cc16"];

const FailPieChart = ({data}) => {
    if (!data?.length) return <NoData/>;
    return (
        <div style={{width:"100%",height:260}}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie data={data} dataKey="count" name="Počet" nameKey="name" innerRadius={55} outerRadius={95}
                         paddingAngle={2}>
                        {data.map((_,i) => <Cell key={i} fill={FAIL_COLORS[i % FAIL_COLORS.length]}/>)}
                    </Pie>
                    <RTooltip
                        contentStyle={{borderRadius:"8px",border:"1px solid #e2e8f0",fontSize: "var(--enigoo-fs-sm)",boxShadow:"0 4px 12px rgba(0,0,0,0.08)"}}
                    />
                    <Legend iconType="circle" wrapperStyle={{fontSize:11,color:"#475569"}}/>
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

// ─── main ─────────────────────────────────────────────────────────────────────

// Formát shodný s DateTimeField (showTimeSelect): "yyyy-MM-ddTHH:mm" (lokální čas, bez TZ)
const pad = (n) => String(n).padStart(2, "0");
const fmtLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const todayStart = () => { const d = new Date(); d.setHours(0,0,0,0); return fmtLocal(d); };
const todayEnd   = () => { const d = new Date(); d.setHours(23,59,0,0); return fmtLocal(d); };

const TurnstileDashboard = () => {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const user = useSelector(s => s.user);
    // Rozsah Od/Do v reduxu (persist přes redux-persist → drží se i po refreshi).
    const {from, to} = useSelector(s => s.dashboardRange);
    const dispatch = useDispatch();

    const isToday = from === todayStart() && to === todayEnd();

    useEffect(() => {
        user.activeAreal && getData(from, to);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.activeAreal]);

    // Tichá varianta getData (bez setLoading) — pro ruční "Obnovit" tlačítko u Stavu služeb,
    // ať klik nepřeblikne celou stránku loaderem.
    const refreshSilently = (f, t) => {
        if (!user.activeAreal || (f && t && t < f)) return;
        apiGetDashboardInfo(user.activeAreal.id, f, t, (response) => setData(response), () => {});
    };

    const getData = (f, t) => {
        if (f && t && t < f) {
            Toast.error('„Do“ nesmí být dřív než „Od“.');
            return;
        }
        setLoading(true);
        apiGetDashboardInfo(user.activeAreal.id, f, t, (response) => {
            setData(response);
            setLoading(false);
        }, () => {
            Toast.error("Chyba při načítání informací systému.");
            setData(null);
            setLoading(false);
        });
    };

    const applyRange = (values) => {
        const f = values?.from, t = values?.to;
        if (f && t && t < f) {
            Toast.error('„Do“ nesmí být dřív než „Od“.');
            return;
        }
        dispatch({type: SET_DASHBOARD_RANGE, payload: {from: f, to: t}});
        getData(f, t);
    };

    const renderData = () => (
        <>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px"}}>
                <SectionLabel first>Stav služeb</SectionLabel>
                <button
                    type="button"
                    onClick={() => refreshSilently(from, to)}
                    title="Obnovit hned, nečekat na automatické obnovení (10s)"
                    style={{display:"flex",alignItems:"center",gap:"4px",background:"none",border:"none",color:"#94a3b8",fontSize:"var(--enigoo-fs-xs)",cursor:"pointer",padding:"0 0 4px"}}
                >
                    <FiRefreshCw size={11}/> Obnovit
                </button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(250px,1fr))",gap:"10px",marginBottom:"4px"}}>
                <ServiceCard label={"Databáze"} value={data.dbState}/>
                {(data.sockets ?? []).map(socket => <SocketCard key={socket.name} socket={socket}/>)}
            </div>

            <SectionLabel>{isToday ? "Vstupy dnes" : "Vstupy ve zvoleném rozsahu"}</SectionLabel>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:"10px",marginBottom:"4px"}}>
                <StatCard label={isToday ? "Unikátní vstupy dnes" : "Unikátní vstupy"} value={data.uniqueEntranceToday}/>
                <StatCard label={isToday ? "Lidé v areálu dnes (úspěšné vstupy)" : "Úspěšné vstupy v rozsahu"} value={data.uniqueSuccessfulEntranceToday}/>
            </div>

            <SectionLabel>{isToday ? "Změny vstupenek dnes" : "Změny vstupenek ve zvoleném rozsahu"}</SectionLabel>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:"10px",marginBottom:"4px"}}>
                <StatCard label={isToday ? "Změny vstupenek dnes" : "Změny vstupenek"} value={data.changesTotal}/>
            </div>

            <SectionLabel>Prostředky</SectionLabel>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:"10px",marginBottom:"4px"}}>
                <ResourceCard
                    label={"Paměť"}
                    used={data.memoryUsed}
                    total={data.memoryTotal}
                    unit={"MB"}
                    percent={data.memoryPercentUsage}
                />
                <ResourceCard
                    label={"Disk"}
                    used={data.usedDiskSpace  ? data.usedDiskSpace.toFixed(1)  : "—"}
                    total={data.totalDiskSpace ? data.totalDiskSpace.toFixed(1) : "—"}
                    unit={"GB"}
                    percent={data.diskUsagePercent}
                />
            </div>

            <SectionLabel>Systém</SectionLabel>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:"10px"}}>
                <InfoCard label={"Server"}          value={data.serverName}/>
                <InfoCard label={"Verze aplikace"}  value={data.appVersion}  sub={data.appBuildTime}/>
                <InfoCard label={"Uptime"}          value={data.uptime}/>
                <InfoCard label={"Java"}            value={data.javaVersion}/>
                <InfoCard label={"OS"}              value={data.osName}/>
                <InfoCard label={"Obrázky"}         value={data.imagesCount} sub={data.imagesSizeAll}/>
            </div>
        </>
    );

    const renderGraphs = () => {
        const failedLabeled = data.failedByResult?.map(item => ({
            ...item,
            name: labelResult(item.name),
        }));
        const changesTypeLabeled = data.changesByType?.map(item => ({
            ...item,
            name: labelChange(item.name),
        }));
        return (
            <>
                <ChartCard title={isToday ? "Průchody dnes – podle zóny" : "Průchody – podle zóny"}>
                    <HBarChart data={data.byZone}/>
                </ChartCard>
                <ChartCard title={isToday ? "Průchody dnes – podle tarifu" : "Průchody – podle tarifu"}>
                    <HBarChart data={data.byTariff}/>
                </ChartCard>
                <ChartCard title={isToday ? "Neúspěšné průchody dnes" : "Neúspěšné průchody v rozsahu"}>
                    <FailPieChart data={failedLabeled}/>
                </ChartCard>
                <ChartCard title={isToday ? "Změny vstupenek dnes – podle typu" : "Změny vstupenek – podle typu"}>
                    <HBarChart data={changesTypeLabeled}/>
                </ChartCard>
                <ChartCard title={isToday ? "Změny vstupenek dnes – podle admina" : "Změny vstupenek – podle admina"}>
                    <HBarChart data={data.changesByAdmin}/>
                </ChartCard>
            </>
        );
    };

    const renderContent = () => {
        if (loading) return <DefaultLoader text={"Načítám informace..."}/>;
        if (!data)   return <div style={{padding:"40px",textAlign:"center",color:"#94a3b8",fontSize: "var(--enigoo-fs-md)"}}>Žádná data</div>;

        return (
            <CustomTabs tabs={[
                {label: "Data",   icon: <FiServer size={13}/>,   content: renderData()},
                {label: "Grafy",  icon: <FiBarChart2 size={13}/>, content: renderGraphs()},
            ]}/>
        );
    };

    return (
        <>
            {user.activeAreal && (
                <>
                    <PageHeader crumbs={[{label: "Dashboard"}]}/>

                    <Filter
                        onSubmit={applyRange}
                        initialValues={{from, to}}
                        loading={loading}
                        extraButtons={({form, submit}) => (
                            <FilterButton
                                type="button"
                                onClick={() => {
                                    form.change("from", todayStart());
                                    form.change("to", todayEnd());
                                    submit();
                                }}
                                text="Dnes"
                                color="outline"
                                icon={<FiBarChart2 size={13}/>}
                            />
                        )}
                    >
                        {({values, submit}) => (
                            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"8px",marginBottom:"4px"}}>
                                <DateTimeField name={"from"} isMandatory={false} valueToCompare={values?.to}
                                    validateFun={(d1, d2) => moment(d1).isAfter(moment(d2)) ? "„Od“ nemůže být po „Do“" : ""}
                                    label={"Od"} type={"from"} placeholder={"Od"} showTimeSelect onEnter={submit}/>
                                <DateTimeField name={"to"} isMandatory={false} valueToCompare={values?.from}
                                    validateFun={(d1, d2) => moment(d2).isAfter(moment(d1)) ? "„Od“ nemůže být po „Do“" : ""}
                                    label={"Do"} type={"to"} placeholder={"Do"} showTimeSelect onEnter={submit}/>
                            </div>
                        )}
                    </Filter>

                    {renderContent()}
                </>
            )}
        </>
    );
};

export default TurnstileDashboard;
